import { Directive, input } from '@angular/core';

export type AncButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'on-primary'
    | 'on-secondary'
    | 'hyperlink'
    | 'underlined';

export type AncButtonSize = 'mini' | 'small' | 'medium' | 'large' | 'default';

/**
 * Figma-accurate button styling applied directly to a native <button>/<a>.
 * Toggles global `.anc-btn` / `.anc-btn--<variant>` / `.anc-btn--<size>` classes
 * (styles in libs/common-interceptor/src/lib/styles/_anc-button.scss). Carries no
 * behaviour — click/disabled/type/ARIA stay on the host element.
 *
 * @example
 * <button libAncButton variant="primary" size="medium" (click)="save()">Save</button>
 */
@Directive({
    selector: '[libAncButton]',
    standalone: true,
    host: {
        class: 'anc-btn',
        '[class.anc-btn--primary]': "variant() === 'primary'",
        '[class.anc-btn--secondary]': "variant() === 'secondary'",
        '[class.anc-btn--tertiary]': "variant() === 'tertiary'",
        '[class.anc-btn--on-primary]': "variant() === 'on-primary'",
        '[class.anc-btn--on-secondary]': "variant() === 'on-secondary'",
        '[class.anc-btn--hyperlink]': "variant() === 'hyperlink'",
        '[class.anc-btn--underlined]': "variant() === 'underlined'",
        '[class.anc-btn--mini]': "size() === 'mini'",
        '[class.anc-btn--small]': "size() === 'small'",
        '[class.anc-btn--medium]': "size() === 'medium'",
        '[class.anc-btn--large]': "size() === 'large'",
        '[class.anc-btn--default]': "size() === 'default'",
        '[class.anc-btn--icon-only]': 'iconOnly()'
    }
})
export class AncButtonDirective {
    /** Visual style. Defaults to the primary filled button. */
    variant = input<AncButtonVariant>('primary');
    /** Size token. Defaults to medium (40px). */
    size = input<AncButtonSize>('medium');
    /** Square icon-only geometry (width matches height for the active size). */
    iconOnly = input<boolean>(false);
}
